'use client';

import { useEffect, useState } from 'react';
import { readWikiToken, WIKI_AUTH_EVENT } from '../lib/wiki-auth';
import { fetchPostEditAccess } from '../lib/guide-article';
import { wikiGuideEditPath, wikiGuidesPath } from '../lib/spa-routes';

/**
 * 权限校验与删除走 API 的 game_key；页面跳转必须用 domain_key（与壳 URL 一致）。
 */
export default function PostEditLink({
  gameKey,
  gameDomain,
  postId,
}: {
  gameKey: string;
  /** URL 段，取 games.domain_key */
  gameDomain: string;
  postId: string;
}) {
  const [canEdit, setCanEdit] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleteError, setDeleteError] = useState('');
  const domain = gameDomain || gameKey;

  useEffect(() => {
    let cancelled = false;
    let requestId = 0;
    const check = () => {
      const currentRequest = ++requestId;
      const token = readWikiToken();
      setCanEdit(false);
      if (!token) return;
      void fetchPostEditAccess(gameKey, postId, token).then((ok) => {
        if (!cancelled && currentRequest === requestId) setCanEdit(ok);
      }).catch(() => {
        if (!cancelled && currentRequest === requestId) setCanEdit(false);
      });
    };
    check();
    window.addEventListener('storage', check);
    window.addEventListener(WIKI_AUTH_EVENT, check);
    return () => {
      cancelled = true;
      window.removeEventListener('storage', check);
      window.removeEventListener(WIKI_AUTH_EVENT, check);
    };
  }, [gameKey, postId]);

  const deletePost = async () => {
    if (deleting) return;
    const token = readWikiToken();
    if (!token) return;
    setDeleteError('');
    setDeleting(true);
    try {
      const response = await fetch(`/api/games/${encodeURIComponent(gameKey)}/posts/${encodeURIComponent(postId)}`, {
        method: 'DELETE',
        headers: { 'access-token': token },
      });
      if (!response.ok) throw new Error('删除失败，请稍后重试');
      window.location.assign(wikiGuidesPath(domain));
    } catch (error) {
      setDeleteError(error instanceof Error ? error.message : '删除失败，请稍后重试');
      setDeleting(false);
    }
  };

  return canEdit ? <>
    <div className="guide-post-actions">
      <a className="guide-post-action" href={wikiGuideEditPath(domain, postId)}>编辑</a>
      <button className="guide-post-action guide-delete-link" type="button" onClick={() => { setDeleteError(''); setConfirmOpen(true); }} disabled={deleting}>
        删除
      </button>
    </div>
    {confirmOpen && <div className="guide-delete-overlay" role="presentation" onMouseDown={(event) => {
      if (event.target === event.currentTarget && !deleting) setConfirmOpen(false);
    }}>
      <section className="guide-delete-dialog" role="dialog" aria-modal="true" aria-labelledby="guide-delete-title">
        <h2 id="guide-delete-title">确认删除帖子？</h2>
        <p>删除后，这篇帖子将不会再出现在文章列表中。</p>
        {deleteError && <p className="guide-delete-error" role="alert">{deleteError}</p>}
        <div className="guide-delete-dialog-actions">
          <button type="button" onClick={() => setConfirmOpen(false)} disabled={deleting}>取消</button>
          <button type="button" className="guide-delete-confirm" onClick={deletePost} disabled={deleting}>
            {deleting ? '删除中…' : '确认删除'}
          </button>
        </div>
      </section>
    </div>}
  </> : null;
}
